import React, { useCallback, useContext, useEffect, useState } from "react";
import { Badge, Box, Flex, Spinner } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { mnt, mntCard, mntType } from "theme/maintenanceTokens";
import UserContext from "context/User";

import { getLocationById, getMapsAtLocation } from "api/Locations";
import { getPortalLocations } from "api/LocationClientProfiles";
import { getWorkOrders } from "api/ServicePlans";
import RequestWorkModal from "containers/Maintenance/components/RequestWorkModal";
import {
  lifecycleBadge,
  workOrderDisplayIdentity,
} from "containers/Maintenance/components/workOrderPresentation";

/**
 * CLIENT view of a location (Session 62) - a lean, read-only site page
 * rendered on the SAME route as the operator Location detail
 * (`/locations/:id`), gated by role in the route entry.
 *
 * WHY THIS EXISTS: the operator `LocationDetail` is a full site console -
 * archive/delete/merge, map import/export, the RFQ/Quote/Project/Invoice/
 * PO records chain, attach-map modals. Every one of those reads is
 * `@Permissions(["isCompany"])` server-side, so a client hitting the
 * operator page would 403 on most of its fetches AND see operator-only
 * controls. This component renders only what a client is permitted and
 * needs: the site's identity, and their own work orders at it.
 *
 * The two client entry points into this page (Jack's model):
 *   1. the Locations nav item (orgs with the portal/calculator enabled), and
 *   2. a maintenance plan's Locations table (ANY client with a plan).
 * Both point here; the only difference between org types is which entry
 * points exist, not the page.
 *
 * DATA: `getLocationById` allows a client and enforces org ownership
 * server-side (404, not 403, when not theirs - no enumeration signal).
 * `getWorkOrders({ locationId })` is client-scoped in WorkOrderService
 * (org scope + location anchor) and field-stripped in the controller, so
 * this component only ever receives client-safe WO rows. A site with
 * several work orders lists them ALL here - the fix for the plan
 * Locations row that used to open just one of them.
 *
 * The WO row mirrors the client plan WO table (PlanClientDetail): same
 * `lifecycleBadge` / `workOrderDisplayIdentity` presentation helpers and
 * the same theme-ui `<Badge variant>` status chip, so a work order reads
 * identically whether the client reaches it from the plan or from here.
 */
export default function ClientLocationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext<any>(UserContext);

  const [location, setLocation] = useState<any>(null);
  const [loadingLocation, setLoadingLocation] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(true);

  /**
   * Request work / Create work order from the site itself.
   *
   * PLAN MEMBERSHIP COMES FROM THE SITE, NOT FROM ITS WORK ORDERS.
   * This used to read the plan off `workOrders[].servicePlan`, which
   * meant a site with no work order yet had no plan reference and got
   * no actions at all — exactly the site a client is most likely to be
   * standing on when they decide something needs doing. `/portal/
   * locations` already resolves `plan { id, name, selfServiceEnabled }`
   * per site server-side (it is what draws the plan badge on the client
   * Locations list — shown only when a plan actually covers the site;
   * the client Locations list), so the answer was one existing
   * client-permitted call away. The work orders remain the fallback if
   * that call fails.
   */
  const [sitePlan, setSitePlan] = useState<any>(null);
  // The org's self-service plan, whether or not THIS site is on it.
  // A client whose org has the grant can raise work at any of their
  // sites; the modal states plainly when a site is off-plan and will be
  // quoted on its own. Gating the buttons on this site's membership hid
  // the faster path on every off-plan site for no reason.
  const [orgPlan, setOrgPlan] = useState<any>(null);
  const [planResolved, setPlanResolved] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestMode, setRequestMode] = useState<"request" | "order">("request");

  /**
   * Maps already measured at this site.
   *
   * Before this, a site with a saved map looked identical to one with
   * none: the only way to find out was to press "Measure it in the mulch
   * calculator" and discover zones already drawn. So a client could
   * re-measure a site someone on their team had already done, and the
   * yardage they had paid attention to was invisible on the page about
   * that site.
   *
   * Same read the operator case file uses (`getMapsAtLocation`), so the
   * two surfaces cannot disagree about what exists here.
   */
  const [siteMaps, setSiteMaps] = useState<any[]>([]);
  useEffect(() => {
    if (!id) return;
    let alive = true;
    getMapsAtLocation(id, false)
      .then((rows: any) => {
        if (alive) setSiteMaps(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        // A missing map list must never cost the page. The site, its
        // actions and its work orders are the point; this is context.
        if (alive) setSiteMaps([]);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const woDerivedPlan =
    workOrders.find((w: any) => w?.servicePlan?.clientSelfServiceEnabled)
      ?.servicePlan || null;
  const selfServicePlan = sitePlan?.selfServiceEnabled
    ? { id: sitePlan.id, name: sitePlan.name }
    : woDerivedPlan ??
      (orgPlan ? { id: orgPlan.id, name: orgPlan.name } : null);

  // Same parent-aware resolution the sidebar uses for the Mulch
  // Calculator / Locations nav items (NavbarLayout). The capability
  // lives on the client ORGANIZATION; a user under that org reads it
  // off `.parent`, an individual customer off their own record. Reading
  // it only off `user.client` would hide the map action from every user
  // in an org — the common case.
  const calculatorEnabled =
    user?.client?.parent?.portalLocationsEnabled === true ||
    (user?.client && !user?.client?.parent
      ? user?.client?.portalLocationsEnabled === true
      : false);

  const openRequest = (mode: "request" | "order") => {
    setRequestMode(mode);
    setRequestOpen(true);
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Location";
  }, []);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoadingLocation(true);
    getLocationById(id)
      .then((loc: any) => {
        if (!alive) return;
        if (loc?.id) {
          setLocation(loc);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
        setLoadingLocation(false);
      })
      .catch(() => {
        if (alive) {
          setNotFound(true);
          setLoadingLocation(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const loadWorkOrders = useCallback(async () => {
    if (!id) return;
    setLoadingWorkOrders(true);
    // excludeInterceptor: the locationId filter degrades this one list
    // rather than taking the whole page to the 404 screen on an older API.
    const res: any = await getWorkOrders({
      query: { locationId: id },
      excludeInterceptor: true,
    });
    const rows: any[] = Array.isArray(res)
      ? res
      : Array.isArray(res?.items)
      ? res.items
      : [];
    // A work order the client has asked us to cancel sorts to the TOP.
    // It is the only row on this table where THEY are waiting on US for
    // an answer, and on a site with four work orders it should not be
    // something they have to hunt for. Everything else keeps the order
    // the server returned.
    setWorkOrders(
      [...rows].sort((a: any, b: any) => {
        const aPending = a?.cancellationRequestedAt ? 1 : 0;
        const bPending = b?.cancellationRequestedAt ? 1 : 0;
        return bPending - aPending;
      }),
    );
    setLoadingWorkOrders(false);
  }, [id]);

  useEffect(() => {
    loadWorkOrders();
  }, [loadWorkOrders]);

  // Which plan covers this site, if any. Resolved from the portal
  // locations read rather than inferred from work orders — see the note
  // on `sitePlan` above. Failure is not fatal: the actions fall back to
  // the work-order-derived plan and the page renders as before.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    // ONE row, not the whole list. This used to fetch every site the
    // client owns and find this one in the array — fine at four sites,
    // 1,811 rows for a client like Playground Boss to answer a question
    // about a single site. The endpoint takes `locationId` now.
    getPortalLocations({ locationId: id, pageSize: 1 })
      .then((res: any) => {
        if (!alive) return;
        const row = (res?.items ?? [])[0];
        setSitePlan(row?.plan ?? null);
        // The ORG's grant, answered by the server rather than by
        // scanning every site the client owns.
        setOrgPlan(res?.orgSelfServicePlan ?? null);
        setPlanResolved(true);
      })
      .catch(() => {
        if (alive) setPlanResolved(true);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (loadingLocation) {
    return (
      <>
        <Header></Header>
        <Page title="Location">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  if (notFound || !location) {
    return (
      <>
        <Header></Header>
        <Page title="Location">
          <Box
            sx={{
              ...mntCard,
              padding: "24px",
              textAlign: "center",
              maxWidth: "600px",
              marginX: "auto",
            }}
          >
            <Subtext
              fontSize={"14px"}
              fontFamily={"Poppins"}
              margin={0}
              color={mnt.textMuted}
            >
              This location could not be found on your account.
            </Subtext>
          </Box>
        </Page>
      </>
    );
  }

  const address = location?.address ?? null;
  const name = location?.name || address?.line1 || "Location";
  const addressLine = address
    ? [address.line1, address.city, address.state, address.postalCode]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <>
      <Header></Header>
      <Page title={name} $noPadding={true}>
        <Box
          sx={{
            maxWidth: "980px",
            marginX: "auto",
            width: "100%",
            paddingBottom: "40px",
          }}
        >
          {/* Header */}
          <Box sx={{ marginTop: "16px", marginBottom: "18px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                fontFamily: "Poppins",
                fontSize: "11px",
                color: mnt.link,
                cursor: "pointer",
                marginBottom: "8px",
              }}
            >
              {"\u2039 Back"}
            </button>
            <H5
              fontFamily="Poppins"
              fontWeight={600}
              margin={0}
              padding={0}
              fontSize={"22px"}
            >
              {name}
            </H5>
            {addressLine && (
              <Subtext
                fontSize={"13px"}
                fontFamily={"Poppins"}
                marginTop={"5px"}
                marginBottom={0}
                color={mnt.textMuted}
              >
                {addressLine}
              </Subtext>
            )}

            {/* WHAT CAN I DO HERE. A client standing on a site is at the
                moment of deciding something needs doing; the page used
                to offer nothing at all unless the site already had a
                work order. What is on offer depends on the site:
                  - the map, whenever the org has the calculator, and
                  - raising work, when the site is on a self-service plan.
                A site NOT on a plan says so plainly rather than showing
                a control that would fail. */}
            <Flex
              sx={{
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
                marginTop: "14px",
              }}
            >
              {calculatorEnabled && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      siteMaps.length
                        ? `/mulch-calculator/${id}/maps/${siteMaps[0].mapGroupId}/edit`
                        : `/mulch-calculator/${id}/edit`,
                    )
                  }
                  style={{
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    fontWeight: 500,
                    padding: "7px 14px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    background: "transparent",
                    border: "1px solid " + mnt.divider,
                    color: mnt.ink,
                  }}
                >
                  {siteMaps.length
                    ? "Open the map"
                    : "Measure it in the mulch calculator"}
                </button>
              )}

              {selfServicePlan && (
                <>
                  <button
                    type="button"
                    onClick={() => openRequest("request")}
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 500,
                      padding: "7px 14px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: "transparent",
                      border: "1px solid " + mnt.divider,
                      color: mnt.ink,
                    }}
                  >
                    Request work
                  </button>
                  <button
                    type="button"
                    onClick={() => openRequest("order")}
                    style={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 500,
                      padding: "7px 14px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      background: mnt.ink,
                      border: "1px solid " + mnt.ink,
                      color: "#fff",
                    }}
                  >
                    Create work order
                  </button>
                </>
              )}

              {planResolved && !sitePlan && (
                <Subtext
                  fontSize={"11.5px"}
                  fontFamily={"Poppins"}
                  margin={0}
                  color={mnt.textMuted}
                >
                  This site is not on a maintenance plan, so anything raised
                  here is quoted and invoiced on its own.
                </Subtext>
              )}
            </Flex>
          </Box>

          {/* MAPS AT THIS SITE (Session 80 follow-up). A site with a
              saved map used to look exactly like one without: the only
              way to find out was to press the calculator button and find
              zones already drawn. The yardage is the number the whole
              quote hangs off, so it belongs on the page about the site,
              not one click inside a tool.

              CALLED "MAPS", NOT "MEASUREMENTS". The record is a map
              everywhere else it appears — the `mulch_map` entity, the
              /maps/:mapGroupId/edit route, the operator case file's
              "MAPS AT THIS SITE" card, and the calculator's own "Save as
              new map" button, which the CLIENT sees. Two names for one
              object inside a single flow is how a client ends up unsure
              whether they are looking at the same thing. The verb stays
              a verb ("Measure it…" invites the work); the noun is the
              record. */}
          {siteMaps.length > 0 && (
            <Box sx={{ marginBottom: "22px" }}>
              <H5
                fontFamily="Poppins"
                fontWeight={600}
                margin={0}
                fontSize={"15px"}
                sx={{ marginBottom: "10px" }}
              >
                Maps
              </H5>
              <Box sx={{ ...mntCard, overflow: "hidden" }}>
                {siteMaps.map((m: any, idx: number) => (
                  <Flex
                    key={m.mapGroupId || idx}
                    onClick={() =>
                      navigate(`/mulch-calculator/${id}/maps/${m.mapGroupId}/edit`)
                    }
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      padding: "11px 16px",
                      cursor: "pointer",
                      borderBottom:
                        idx === siteMaps.length - 1
                          ? "none"
                          : "0.5px solid " + mnt.divider,
                      "&:hover": { background: mnt.bgSubtle },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Span
                        fontFamily={"Poppins"}
                        fontSize={"12.5px"}
                        sx={{ color: mnt.link, fontWeight: 500 }}
                      >
                        {m.name || "Untitled map"}
                      </Span>
                      <Box sx={{ ...mntType.labelSm, marginTop: "2px" }}>
                        {m.zoneCount} zone{m.zoneCount === 1 ? "" : "s"}
                        {m.updatedAt ? ` \u00b7 updated ${fmtDate(m.updatedAt)}` : ""}
                      </Box>
                    </Box>
                    <Span
                      fontFamily={"Poppins"}
                      fontSize={"13px"}
                      sx={{ color: mnt.ink, fontWeight: 600, flexShrink: 0 }}
                    >
                      {Number(m.totalCuYd || 0).toFixed(1)} cu yd
                    </Span>
                  </Flex>
                ))}
              </Box>
            </Box>
          )}

          {/* Work orders at this site */}
          <Box sx={{ marginBottom: "22px" }}>
            <Flex
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <H5
                fontFamily="Poppins"
                fontWeight={600}
                margin={0}
                fontSize={"15px"}
              >
                Work orders
              </H5>
              {/* The two plan actions now live in the page header action
                  row above, where they are reachable on a site with no
                  work orders yet — which was the whole problem. */}
            </Flex>

            <Box sx={{ ...mntCard, overflow: "hidden" }}>
              <Flex
                sx={{
                  padding: "10px 16px",
                  background: mnt.bgSubtle,
                  borderBottom: "0.5px solid " + mnt.divider,
                  gap: "10px",
                }}
              >
                <Box sx={{ flex: "0 0 30%", ...mntType.labelSm, fontWeight: 600 }}>
                  WORK ORDER
                </Box>
                <Box sx={{ flex: "0 0 28%", ...mntType.labelSm, fontWeight: 600 }}>
                  SERVICE
                </Box>
                <Box sx={{ flex: "0 0 20%", ...mntType.labelSm, fontWeight: 600 }}>
                  RECEIVED
                </Box>
                <Box sx={{ flex: "0 0 22%", ...mntType.labelSm, fontWeight: 600 }}>
                  STATUS
                </Box>
              </Flex>

              {loadingWorkOrders ? (
                <Box sx={{ textAlign: "center", paddingY: "36px" }}>
                  <Spinner />
                </Box>
              ) : workOrders.length === 0 ? (
                <Box sx={{ padding: "34px", textAlign: "center" }}>
                  <Subtext
                    fontSize={"12px"}
                    fontFamily={"Poppins"}
                    margin={0}
                    color={mnt.textMuted}
                  >
                    No work orders at this location yet.
                  </Subtext>
                </Box>
              ) : (
                workOrders.map((wo, idx) => {
                  const badge = lifecycleBadge(wo);
                  const identity = workOrderDisplayIdentity(wo);
                  const service = wo.service?.name || "-";
                  const received = wo.receivedAt
                    ? fmtDate(wo.receivedAt)
                    : "-";
                  return (
                    <Flex
                      key={wo.id || idx}
                      onClick={() =>
                        navigate("/maintenance/work-orders/" + wo.id)
                      }
                      sx={{
                        alignItems: "center",
                        gap: "10px",
                        padding: "11px 16px",
                        cursor: "pointer",
                        borderBottom:
                          idx === workOrders.length - 1
                            ? "none"
                            : "0.5px solid " + mnt.divider,
                        "&:hover": { background: mnt.bgSubtle },
                      }}
                    >
                      <Box sx={{ flex: "0 0 30%", minWidth: 0 }}>
                        <Span
                          fontFamily={"Poppins"}
                          fontSize={"12.5px"}
                          sx={{ color: mnt.link, fontWeight: 500 }}
                        >
                          {identity.primary}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 28%", minWidth: 0 }}>
                        <Span
                          fontFamily={"Poppins"}
                          fontSize={"12px"}
                          color={mnt.textMuted}
                        >
                          {service}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 20%", minWidth: 0 }}>
                        <Span
                          fontFamily={"Poppins"}
                          fontSize={"12px"}
                          color={mnt.textMuted}
                        >
                          {received}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 22%", minWidth: 0 }}>
                        {/* Session 80 follow-up — a pending cancellation
                            REPLACES the lifecycle badge rather than
                            sitting beside it. It is the only thing about
                            this row the client is waiting on us for, and
                            two chips would make them read both to work
                            out which one matters. */}
                        {wo?.cancellationRequestedAt ? (
                          <Box
                            sx={{
                              display: "inline-block",
                              fontFamily: "Poppins",
                              fontSize: "11px",
                              fontWeight: 500,
                              padding: "3px 9px",
                              borderRadius: "10px",
                              whiteSpace: "nowrap",
                              background: mnt.amberPill,
                              color: mnt.amberDeep,
                            }}
                          >
                            Cancellation requested
                          </Box>
                        ) : (
                          <Badge as="span" variant={badge.variant as any}>
                            {badge.label}
                          </Badge>
                        )}
                      </Box>
                    </Flex>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </Page>

      {requestOpen && selfServicePlan && (
        <RequestWorkModal
          planId={selfServicePlan.id}
          planName={selfServicePlan.name}
          mode={requestMode}
          // The site is already known — no picker.
          presetSite={{
            locationId: id,
            name: location?.name || location?.address?.line1 || "This site",
            city: location?.address?.city || "",
            onPlan: true,
            planName: selfServicePlan.name,
          }}
          onClose={() => setRequestOpen(false)}
          onCreated={() => {
            setRequestOpen(false);
            // Refresh so the new work order appears in the list below.
            if (id) {
              getWorkOrders({ query: { locationId: id } }).then((res: any) => {
                setWorkOrders(Array.isArray(res) ? res : res?.items || []);
              });
            }
          }}
        />
      )}
    </>
  );
}

function fmtDate(d: any): string {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "-";
  }
}
